/* 2009-2018 Copyright Ridecell Inc. All rights reserved. */
export default class SweetAlert2 {
  constructor(lib, messages, onLogout) {
    this.lib = lib
    this.messages = messages
    this.onLogout = onLogout
  }

  /**
   * @param {String} title
   * @param {String} text
   * @returns {Promise}
   */
  success(title, text) {
    return this.lib({
      confirmButtonColor: '#22aaff',
      text,
      titleText: title,
      type: 'success',
    })
  }

  /**
   * @param {String} title
   * @param {String} confirmButtonText
   * @param {Object} [options]
   * @returns {Promise.<Boolean>}
   */
  confirm(title, confirmButtonText, options = {}) {
    const defaultConfirmButtonText = this.messages
      ? this.messages.confirmButtonDefaultText()
      : 'Yes'
    const confirmButtonTextResult = confirmButtonText || defaultConfirmButtonText
    return this.lib({
      cancelButtonText: this.messages ? this.messages.confirmCancelButtonText() : 'No',
      confirmButtonColor: '#22aaff',
      confirmButtonText: confirmButtonTextResult,
      showCancelButton: true,
      showLoaderOnConfirm: false,
      titleText: title,
      type: 'warning',
      ...options,
    })
  }
  close() {
    return this.lib.close()
  }
  /**
   * @param {String} title
   * @param {String} confirmButtonText
   * @returns {Promise.<Boolean>}
   */
  confirmNoUndo(title, confirmButtonText, preConfirm) {
    return this.lib({
      cancelButtonText: this.messages ? this.messages.confirmNoUndoCancelButtonText() : 'No',
      confirmButtonColor: '#22aaff',
      confirmButtonText,
      preConfirm,
      showCancelButton: true,
      showLoaderOnConfirm: true,
      text: this.messages
        ? this.messages.confirmNoUndoText()
        : 'You will not be able to undo this action!',
      titleText: title,
      type: 'warning',
    })
  }

  /**
   * @param {String} objectName
   * @returns {Promise.<Boolean>}
   */
  confirmDelete(objectName, preConfirm, message) {
    return this.lib({
      cancelButtonText: this.messages ? this.messages.confirmDeleteCancelButtonText() : 'No',
      confirmButtonColor: '#22aaff',
      confirmButtonText: this.messages
        ? this.messages.confirmDeleteConfirmButtonText()
        : 'Yes, delete it!',
      preConfirm,
      showCancelButton: true,
      showLoaderOnConfirm: true,
      text:
        message ||
        (this.messages
          ? this.messages.confirmDeleteText(objectName)
          : `Are you sure you want to delete ${objectName}?`),
      titleText: objectName,
      type: 'warning',
    })
  }

  /**
   * @param {String} objectName
   * @returns {Promise}
   */
  deleteSuccess(objectName) {
    return this.lib({
      confirmButtonColor: '#22aaff',
      text: this.messages
        ? this.messages.deleteSuccessText(objectName)
        : `${objectName} has been deleted.`,
      titleText: this.messages ? this.messages.deleteSuccessTitle() : 'Deleted!',
      type: 'success',
    })
  }

  error(message, options = {}) {
    return this.lib({
      confirmButtonColor: '#22aaff',
      text: message,
      titleText: this.messages ? this.messages.errorTitle() : 'Error',
      type: 'warning',
      ...options,
    })
  }

  handleError(xhr) {
    if (xhr.status === 401 && this.onLogout) {
      return this.onLogout()
    }
    if ([401, 403, 404].indexOf(xhr.status) !== -1) {
      return Promise.resolve(xhr)
    }
    if (xhr.status >= 400 && xhr.status < 500) {
      if (xhr.data && xhr.data.non_field_errors && xhr.data.non_field_errors.length > 0) {
        return this.error(xhr.data.non_field_errors)
      } else if (xhr.response || xhr.responseText || xhr.data) {
        const responseData = xhr.response || xhr.data || JSON.parse(xhr.responseText || '{}')
        if (responseData.non_field_errors && responseData.non_field_errors.length > 0) {
          return this.error(responseData.non_field_errors)
        } else if (responseData.detail) {
          return this.error(responseData.detail)
        } else if (responseData.message) {
          return this.error(responseData.message)
        } else if (responseData.user_message) {
          return this.error(responseData.user_message)
        } else if (typeof responseData === 'string') {
          return this.error(responseData)
        } else if (responseData.constructor === Array) {
          return this.error(responseData.join(', '))
        }
        const erroredField = Object.keys(responseData)[0]
        let erroredFieldMessage = responseData[erroredField]
        if (typeof erroredFieldMessage === 'object') {
          // Seems brittle, but this is how the backend sends nested field errors
          erroredFieldMessage = JSON.stringify(erroredFieldMessage)
        }
        return this.error(
          this.messages
            ? this.messages.handleErrorSpecific(erroredField, erroredFieldMessage)
            : `Sorry, invalid ${erroredField
                .replace(/_/g, ' ')
                .replace(/\d/g, '')}: ${erroredFieldMessage}`
        )
      }
    }
    return this.error(
      this.messages
        ? this.messages.handleErrorGeneric()
        : 'Sorry, something went wrong, please try again soon.'
    )
  }
}
